import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJWTAuth } from '../contexts/useJWTAuth';

interface TokenExpiredEvent {
  provider: string;
  message: string;
  errorCode: string;
}

export const OAuthTokenExpiredModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [eventData, setEventData] = useState<TokenExpiredEvent | null>(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useJWTAuth();

  // connection_required is unambiguously a repository-provider issue (scan flow),
  // so deep-link to that tab. Other reconnect codes (e.g. token_expired) may be
  // app-auth, so leave them at the neutral default until routing is purpose-aware.
  const reconnectPath = (): string =>
    eventData?.errorCode === 'connection_required' ? '/settings?tab=repository' : '/settings';

  useEffect(() => {
    const handleTokenExpired = (event: Event) => {
      const customEvent = event as CustomEvent<TokenExpiredEvent>;
      setEventData(customEvent.detail);
      setIsOpen(true);
    };

    window.addEventListener('oauth:token-expired', handleTokenExpired);

    return () => {
      window.removeEventListener('oauth:token-expired', handleTokenExpired);
    };
  }, []);

  // If modal is open but JWT auth is also lost, close modal and let
  // the session-expired flow handle it (ProtectedRoute → /signin)
  useEffect(() => {
    if (isOpen && !isAuthenticated) {
      setIsOpen(false);
      if (!sessionStorage.getItem('auth_return_url')) {
        sessionStorage.setItem('auth_return_url', '/settings');
      }
    }
  }, [isOpen, isAuthenticated]);

  const handleReconnect = () => {
    setIsOpen(false);
    if (!isAuthenticated) {
      // JWT also expired — save return destination for after sign-in
      if (!sessionStorage.getItem('auth_return_url')) {
        sessionStorage.setItem('auth_return_url', reconnectPath());
      }
      return;
    }
    navigate(reconnectPath());
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isOpen || !eventData) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <svg
              className="w-6 h-6 text-yellow-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Account Connection Required
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{eventData.message}</p>
            {eventData.provider && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Provider: <span className="font-medium capitalize">{eventData.provider}</span>
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={handleReconnect}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Reconnect Account
          </button>
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};
